import React, { useCallback, useEffect, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Page } from '@progress/kendo-react-dropdowns'
import {
  GridColumn as Column,
  Grid,
  GridCellProps,
  GridColumnProps,
  GridExpandChangeEvent,
  GridPageChangeEvent,
} from '@progress/kendo-react-grid'
import {
  WelcomeAccountIpActivityDto,
  WelcomeAccountIpActivityDtoKey,
} from 'api/Trading-Accounts/Types/WelcomeAccountIpActivityDto'
import { pageableSettings } from 'store/grid/grid.slice'
import { useApiClient } from 'utils/api/ApiClient'
import { TradingAccountsApiClient } from 'utils/api/tradingAccountsApi'
import { useFetchOne } from 'utils/api/useFetch'
import { showIpCheckerWindow } from 'utils/media-window/showIpCheckerWindow'
import { getPaginationInitialState } from 'utils/paginationInitialState'
import { TableWithExport } from 'utils/table/withExport'

import { WelcomeAccountsDetailTable } from './LeadWelcomeAccountsAnalyticsDetailTable'

interface GridColumn extends GridColumnProps {
  show: boolean
  width?: number
}

const columns: GridColumn[] = [
  {
    title: 'IP Address',
    field: WelcomeAccountIpActivityDtoKey.ipAddress,
    cell: (props: GridCellProps): JSX.Element => {
      return (
        <td className='table-columns__content' align='left' style={{ textAlign: 'left' }}>
          <Button variant='link' onClick={() => showIpCheckerWindow(props.dataItem.ipAddress)}>
            {props.dataItem.ipAddress}
          </Button>
        </td>
      )
    },
    className: 'table-columns__content',
    show: true,
  },
  {
    title: 'Number of Welcome Accounts',
    field: WelcomeAccountIpActivityDtoKey.count,
    className: 'table-columns__number',
    show: true,
  },
]

interface WelcomeTradingAccount {
  id: string
  name: string
}

interface LeadWelcomeAccountsActivityProps {
  activities: WelcomeAccountIpActivityDto[]
  paginationState: Page
  onExpandChange(event: GridExpandChangeEvent): void
  onGridPageChange(event: GridPageChangeEvent): void
}

export const LeadWelcomeAccountsAnalyticsIPAddressUI: React.FC<LeadWelcomeAccountsActivityProps> = (
  props
) => {
  const { activities, paginationState, onExpandChange, onGridPageChange } = props

  return (
    <div style={{ margin: 16 }}>
      <TableWithExport data={activities} columns={columns}>
        <Grid
          resizable
          data={activities.slice(paginationState.skip, paginationState.take + paginationState.skip)}
          skip={paginationState.skip}
          take={paginationState.take}
          total={activities.length}
          detail={WelcomeAccountsDetailTable}
          onPageChange={onGridPageChange}
          onExpandChange={onExpandChange}
          pageable={pageableSettings}
          expandField='expanded'
          style={{ zIndex: 0 }}
        >
          {columns
            .filter((c) => c.show)
            .map((column, idx) => (
              <Column key={idx + 2} {...column} />
            ))}
        </Grid>
      </TableWithExport>
    </div>
  )
}

interface OuterProps {
  data: WelcomeTradingAccount
}

export const LeadWelcomeAccountsAnalyticsIPAddress: React.FC<OuterProps> = (props) => {
  const tradingAccountsApiClient = useApiClient(TradingAccountsApiClient)
  const [activities, setActivities] = useState<WelcomeAccountIpActivityDto[]>([])
  const [paginationState, setPaginationState] = useState<Page>(getPaginationInitialState())

  const callback = useCallback(() => {
    return tradingAccountsApiClient.getWelcomeTradingAccountIpAddressActivity(props.data.id)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps
  const { data } = useFetchOne(callback)

  useEffect(() => {
    if (data) {
      setActivities(data)
    }
  }, [data])

  const handleExpandChange = (event: GridExpandChangeEvent) => {
    setActivities(
      activities.map((x) =>
        x.ipAddress === event.dataItem.ipAddress
          ? {
              ...x,
              expanded: event.value,
            }
          : x
      )
    )
  }

  const handleGridPageChange = (event: GridPageChangeEvent) => {
    setPaginationState({
      skip: event.page.skip,
      take: event.page.take,
    })
  }

  return (
    <LeadWelcomeAccountsAnalyticsIPAddressUI
      activities={activities}
      paginationState={paginationState}
      onExpandChange={handleExpandChange}
      onGridPageChange={handleGridPageChange}
    />
  )
}
